// @flow
// $FlowFixMe - Cannot import useState because there is no useState export in react.
import React, { useState } from 'react';
import { showServerError } from 'bk-web-app';
import { toastError, toastSuccess } from 'bench-internal-theme';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
// $FlowFixMe - TS file
import { SplitFeatureFlags } from '../../features';
import LedgerAccountsTableRow from './LedgerAccountsTableRow';
import type { LedgerAccount, LedgerCategory } from '../types';
import {
  DELETE_LEDGER_ACCOUNT,
  GET_LEDGER_ACCOUNTS,
  GET_LEDGER_ACCOUNTS_BY_CLIENT,
} from '../queries';
import { useMutation } from '@apollo/client';
import { useClient } from '../../ClientContext';
import { useDrop } from 'react-dnd';
import EmptyTableRow from '../EmptyTableRow';

type LedgerAccountsTableRowContainerProps = {
  ledgerAccount: LedgerAccount,
  search: string,
  ledgerCategory: LedgerCategory,
  changeOrder: (string, string, boolean) => void,
  isSorting: boolean,
  isSortSaving: boolean,
  canSort: boolean,
};

const LedgerAccountsTableRowContainer = ({
  ledgerAccount,
  search,
  ledgerCategory,
  changeOrder,
  isSorting,
  isSortSaving,
  canSort,
}: LedgerAccountsTableRowContainerProps) => {
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: `${ledgerCategory.name}-row`,
      canDrop: monitor => monitor.id !== ledgerAccount.id && isSorting && canSort,
      drop: monitor => changeOrder(monitor.id, ledgerAccount.id, false),
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [isSorting, canSort],
  );

  const client = useClient();

  const [featureState] = useFeatureFlags();
  const {
    featureContext: { features },
  } = featureState;

  const isAssignCategoryImprovementEnabled =
    features[SplitFeatureFlags.ASSIGN_CATEGORY_IMPROVEMENT]?.value === FeatureValue.FEATURE_ON;

  // Use v2 query for refetch if feature flag is enabled, otherwise use v1
  const ledgerAccountsQuery = isAssignCategoryImprovementEnabled
    ? GET_LEDGER_ACCOUNTS_BY_CLIENT
    : GET_LEDGER_ACCOUNTS;

  const [isDisplayGroupSaving, setIsDisplayGroupSaving] = useState(false);

  const [deleteLedgerAccount] = useMutation(DELETE_LEDGER_ACCOUNT, {
    onCompleted: () => {
      toastSuccess('Ledger Account Deleted', 'Your ledger account has been deleted.');
    },
    onError: error => {
      toastError('An error occurred deleting this ledger account.', showServerError(error));
    },
    refetchQueries: [{ query: ledgerAccountsQuery, variables: { clientId: client.id } }],
  });

  return (
    <div ref={drop}>
      {isSorting && canSort && canDrop && isOver && <EmptyTableRow isOver={isOver} />}
      <LedgerAccountsTableRow
        isSortSaving={isSortSaving || isDisplayGroupSaving}
        deleteLedgerAccount={() =>
          deleteLedgerAccount({ variables: { ledgerAccountId: ledgerAccount.id } })
        }
        ledgerAccount={ledgerAccount}
        search={search}
        ledgerCategory={ledgerCategory}
        isSorting={isSorting}
        canSort={canSort}
        isAssignCategoryImprovementEnabled={isAssignCategoryImprovementEnabled}
        onDisplayGroupSaveStart={() => setIsDisplayGroupSaving(true)}
        onDisplayGroupSaveComplete={() => setIsDisplayGroupSaving(false)}
      />
    </div>
  );
};

export default LedgerAccountsTableRowContainer;
